import { Check, ChevronDown, ChevronRight, Copy, Key, Loader2, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  expires_at: string | null;
  last_used_at: string | null;
  is_revoked: boolean;
  created_at: string;
}

interface ApiKeyCreated extends ApiKey {
  key: string;
}

// Scopes a user can grant to an API key. Keep in sync with the backend's
// VALID_SCOPES (backend/src/api/routes/api_keys.py).
const AVAILABLE_SCOPES: ReadonlyArray<{ id: string; label: string; description: string }> = [
  { id: "completions", label: "Completions", description: "Chat completions API access (OpenAI-compatible)." },
  { id: "harness", label: "Harness", description: "Register a RALLY connector (remote machine) and report status." },
];

function CreatedKeyBanner({
  plainKey,
  onDismiss,
}: Readonly<{
  plainKey: string;
  onDismiss: () => void;
}>) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(plainKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mb-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
      <p className="text-sm font-medium text-green-400 mb-2">API key created! Copy it now — it won't be shown again.</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 px-3 py-2 bg-surface-secondary rounded text-sm font-mono text-content-primary break-all">
          {plainKey}
        </code>
        <button
          type="button"
          onClick={handleCopy}
          className="px-3 py-2 bg-surface-secondary hover:bg-interactive-active border border-border-secondary rounded-lg text-sm transition-colors"
        >
          {copied ? (
            <span className="text-green-400 text-xs">Copied!</span>
          ) : (
            <Copy className="h-4 w-4 text-content-secondary" />
          )}
        </button>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="mt-2 text-xs text-content-tertiary hover:text-content-secondary"
      >
        Dismiss
      </button>
    </div>
  );
}

function CreateKeyForm({
  onCreated,
  onCancel,
}: Readonly<{
  onCreated: (key: string) => void;
  onCancel: () => void;
}>) {
  const [name, setName] = useState("");
  const [selectedScopes, setSelectedScopes] = useState<string[]>(["completions"]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleScope = (id: string) => {
    setSelectedScopes((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const handleSubmit = async () => {
    if (!name.trim() || selectedScopes.length === 0) return;
    setCreating(true);
    setError(null);
    try {
      const result = await api.post<ApiKeyCreated>("/api-keys", {
        name: name.trim(),
        scopes: selectedScopes,
      });
      onCreated(result.key);
    } catch {
      setError("Failed to create API key");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="mb-4 p-4 bg-surface-secondary border border-border-secondary rounded-lg">
      {error && (
        <div className="mb-3 p-2 bg-red-500/10 border border-red-500/30 rounded">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}
      <label htmlFor="api-key-name" className="block text-sm font-medium text-content-secondary mb-1">
        Key Name
      </label>
      <div className="flex items-center gap-2">
        <input
          id="api-key-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., My CLI Script"
          className="flex-1 px-3 py-2 bg-surface-primary border border-border-primary rounded-lg text-sm text-content-primary placeholder-content-tertiary focus:outline-none focus:ring-1 focus:ring-accent-primary"
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          autoFocus
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={creating || !name.trim() || selectedScopes.length === 0}
          className="px-4 py-2 bg-accent-primary hover:bg-accent-primary/80 disabled:opacity-50 rounded-lg text-sm font-medium text-white transition-colors"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-2 text-sm text-content-tertiary hover:text-content-secondary"
        >
          Cancel
        </button>
      </div>
      <fieldset className="mt-3">
        <legend className="block text-sm font-medium text-content-secondary mb-1">Scopes</legend>
        <div className="space-y-1.5">
          {AVAILABLE_SCOPES.map((scope) => (
            <label key={scope.id} className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedScopes.includes(scope.id)}
                onChange={() => toggleScope(scope.id)}
                className="mt-0.5 h-4 w-4 rounded border-border-primary bg-surface-primary text-accent-primary focus:ring-1 focus:ring-accent-primary"
              />
              <span className="text-sm text-content-secondary">
                {scope.label}
                <span className="text-content-tertiary"> — {scope.description}</span>
              </span>
            </label>
          ))}
        </div>
        {selectedScopes.length === 0 && <p className="mt-1 text-xs text-red-400">Select at least one scope.</p>}
      </fieldset>
    </div>
  );
}

function KeyList({
  loading,
  keys,
  onRevoke,
}: Readonly<{
  loading: boolean;
  keys: ApiKey[];
  onRevoke: (id: string) => void;
}>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-content-tertiary" />
      </div>
    );
  }

  if (keys.length === 0) {
    return (
      <p className="text-sm text-content-tertiary py-4">
        No API keys yet. Create one to start using Anthm's models programmatically.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {keys.map((k) => (
        <div
          key={k.id}
          className="flex items-center justify-between p-3 bg-surface-secondary border border-border-secondary rounded-lg"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-content-primary">{k.name}</span>
              <code className="text-xs text-content-tertiary font-mono">{k.key_prefix}...</code>
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-content-tertiary">
              <span>Scopes: {k.scopes.join(", ")}</span>
              <span>Created: {new Date(k.created_at).toLocaleDateString()}</span>
              {k.last_used_at && <span>Last used: {new Date(k.last_used_at).toLocaleDateString()}</span>}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onRevoke(k.id)}
            className="p-2 text-content-tertiary hover:text-red-400 transition-colors"
            title="Revoke key"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

function CodeBlock({ code }: Readonly<{ code: string }>) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className="p-3 pr-10 bg-surface-secondary rounded-lg text-xs font-mono text-content-secondary overflow-x-auto whitespace-pre">
        {code}
      </pre>
      <button
        type="button"
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 rounded bg-surface-primary/80 border border-border-secondary opacity-0 group-hover:opacity-100 transition-opacity hover:bg-interactive-active"
        title="Copy to clipboard"
      >
        {copied ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3 text-content-tertiary" />}
      </button>
    </div>
  );
}

function QuickStartGuide() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mt-4 border border-border-secondary rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-surface-secondary hover:bg-interactive-active transition-colors text-left"
      >
        <span className="text-sm font-medium text-content-secondary">Quick Start Guide</span>
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-content-tertiary" />
        ) : (
          <ChevronRight className="h-4 w-4 text-content-tertiary" />
        )}
      </button>
      {expanded && (
        <div className="p-4 space-y-4 border-t border-border-secondary">
          <div>
            <h4 className="text-sm font-medium text-content-primary mb-2">1. Install the OpenAI SDK</h4>
            <CodeBlock code="pip install openai" />
          </div>

          <div>
            <h4 className="text-sm font-medium text-content-primary mb-2">2. Use your API key</h4>
            <CodeBlock
              code={`from openai import OpenAI

client = OpenAI(
    base_url="${window.location.origin}/ext/v1/openai",
    api_key="ak_your-key-here"
)

response = client.chat.completions.create(
    model="tier:fast",
    messages=[
        {"role": "user", "content": "Hello!"}
    ]
)

print(response.choices[0].message.content)`}
            />
          </div>

          <div>
            <h4 className="text-sm font-medium text-content-primary mb-2">3. Streaming</h4>
            <CodeBlock
              code={`stream = client.chat.completions.create(
    model="tier:fast",
    messages=[{"role": "user", "content": "Write a poem"}],
    stream=True
)

for chunk in stream:
    print(chunk.choices[0].delta.content or "", end="")`}
            />
          </div>

          <div>
            <h4 className="text-sm font-medium text-content-primary mb-2">Available models</h4>
            <p className="text-xs text-content-tertiary mb-2">
              Use <code className="text-content-secondary">tier:fast</code> for quick responses or{" "}
              <code className="text-content-secondary">tier:thorough</code> for complex tasks. You can also list all
              available models:
            </p>
            <CodeBlock
              code={`models = client.models.list()
for model in models.data:
    print(model.id)`}
            />
          </div>

          <div className="pt-2 border-t border-border-secondary">
            <p className="text-xs text-content-tertiary">
              All usage is tracked and visible to your organization's admins. The API is fully compatible with the
              OpenAI Python and Node SDKs — any code that works with OpenAI will work with Anthm by changing the{" "}
              <code className="text-content-secondary">base_url</code> and{" "}
              <code className="text-content-secondary">api_key</code>.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export function ApiKeysManager() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchKeys = useCallback(async () => {
    try {
      const data = await api.get<ApiKey[]>("/api-keys");
      setKeys(data);
      setError(null);
    } catch {
      setError("Failed to load API keys");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreated = (key: string) => {
    setCreatedKey(key);
    setShowCreateForm(false);
    fetchKeys();
  };

  const handleRevoke = async (id: string) => {
    if (!confirm("Are you sure you want to revoke this API key? This cannot be undone.")) return;
    setError(null);
    try {
      await api.delete(`/api-keys/${id}`);
      fetchKeys();
    } catch {
      setError("Failed to revoke API key");
    }
  };

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-green-500/20 flex items-center justify-center">
            <Key className="h-5 w-5 text-green-400" />
          </div>
          <div>
            <h3 className="font-medium text-content-primary">API Keys</h3>
            <p className="text-sm text-content-tertiary">
              Use API keys to access Anthm's models programmatically via the OpenAI SDK
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-surface-secondary hover:bg-interactive-active border border-border-secondary rounded-lg text-sm font-medium text-content-primary transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Key
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {createdKey && <CreatedKeyBanner plainKey={createdKey} onDismiss={() => setCreatedKey(null)} />}

      {showCreateForm && <CreateKeyForm onCreated={handleCreated} onCancel={() => setShowCreateForm(false)} />}

      <KeyList loading={loading} keys={keys} onRevoke={handleRevoke} />

      <QuickStartGuide />
    </div>
  );
}
